/**
 * Permission Service for handling media and content access control
 *
 * This service defines a standardized permission model for campaign assets:
 * - PUBLIC: Visible to anyone, even non-authenticated users
 * - CAMPAIGN: Visible to all campaign members (DM and players)
 * - PRIVATE: Visible only to the creator/owner
 * - USER_SPECIFIC: Visible to specified users only
 *
 * Used for images, notes, maps, points of interest, and other campaign content
 */

import { hasPlayer } from '@/utils/campaignPlayers'

// Permission levels
export const PermissionLevel = {
  PUBLIC: 'public', // Accessible to everyone
  CAMPAIGN: 'campaign', // Accessible to campaign members only
  PRIVATE: 'private', // Accessible to owner/creator only
  USER_SPECIFIC: 'specific' // Accessible to specified users
}

class PermissionService {
  /**
   * Create a permission object for a new resource
   * @param {string} creatorId - User ID of the creator
   * @param {string} [permissionLevel=PermissionLevel.CAMPAIGN] - Permission level
   * @param {Array<string>} [specificUsers=[]] - Array of user IDs for USER_SPECIFIC permission
   * @param {string} [campaignId=null] - Campaign ID if resource belongs to a campaign
   * @returns {Object} Permission object to store in database
   */
  createPermission(
    creatorId,
    permissionLevel = PermissionLevel.CAMPAIGN,
    specificUsers = [],
    campaignId = null
  ) {
    if (!creatorId) {
      throw new Error('Creator ID is required')
    }

    // Default permission object
    const permission = {
      level: permissionLevel,
      createdBy: creatorId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }

    // Add campaign ID if provided
    if (campaignId) {
      permission.campaignId = campaignId
    }

    // Add specific users if using USER_SPECIFIC permission
    if (
      permissionLevel === PermissionLevel.USER_SPECIFIC &&
      Array.isArray(specificUsers) &&
      specificUsers.length > 0
    ) {
      permission.specificUsers = [...specificUsers]

      // Always ensure creator has access
      if (!permission.specificUsers.includes(creatorId)) {
        permission.specificUsers.push(creatorId)
      }
    }

    return permission
  }

  /**
   * Update an existing permission object
   * @param {Object} existingPermission - Current permission object
   * @param {string} updaterId - User ID making the update
   * @param {string} [newLevel] - New permission level
   * @param {Array<string>} [specificUsers] - New specific users array
   * @returns {Object} Updated permission object
   */
  updatePermission(existingPermission, updaterId, newLevel, specificUsers) {
    if (!existingPermission || !updaterId) {
      throw new Error('Existing permission and updater ID are required')
    }

    // Create a new object to avoid mutating the existing one
    const updatedPermission = { ...existingPermission }

    // Update permission level if provided
    if (newLevel && Object.values(PermissionLevel).includes(newLevel)) {
      updatedPermission.level = newLevel
    }

    // Update specific users if provided and using USER_SPECIFIC permission
    if (updatedPermission.level === PermissionLevel.USER_SPECIFIC && Array.isArray(specificUsers)) {
      updatedPermission.specificUsers = [...specificUsers]

      // Always ensure creator has access
      if (!updatedPermission.specificUsers.includes(updatedPermission.createdBy)) {
        updatedPermission.specificUsers.push(updatedPermission.createdBy)
      }
    }

    // Update the timestamp
    updatedPermission.updatedAt = new Date().toISOString()
    updatedPermission.updatedBy = updaterId

    return updatedPermission
  }

  /**
   * Check if a user has read access to a resource based on permissions
   * @param {Object} permission - Permission object
   * @param {string|null} userId - User ID to check (null for unauthenticated)
   * @param {Object} campaignData - Campaign data for campaign membership check
   * @returns {boolean} Whether user has access
   */
  canRead(permission, userId, campaignData) {
    if (!permission) return false

    // PUBLIC resources are accessible to everyone
    if (permission.level === PermissionLevel.PUBLIC) {
      return true
    }

    // All other permission levels require authentication
    if (!userId) return false

    // Creator always has access
    if (permission.createdBy === userId) {
      return true
    }

    // Admin users always have access (this would be checked elsewhere)
    // if (userIsAdmin) return true;

    // CAMPAIGN level: check campaign membership
    if (permission.level === PermissionLevel.CAMPAIGN && permission.campaignId && campaignData) {
      // DM has access
      if (campaignData.dm === userId) {
        return true
      }

      if (hasPlayer(campaignData.players, userId)) {
        return true
      }
    }

    // USER_SPECIFIC level: check if user is in the list
    if (
      permission.level === PermissionLevel.USER_SPECIFIC &&
      Array.isArray(permission.specificUsers) &&
      permission.specificUsers.includes(userId)
    ) {
      return true
    }

    // PRIVATE resources are only accessible to creator (handled above)
    return false
  }

  /**
   * Check if a user has write access to a resource based on permissions
   * @param {Object} permission - Permission object
   * @param {string} userId - User ID to check
   * @param {Object} campaignData - Campaign data for DM check
   * @returns {boolean} Whether user has write access
   */
  canWrite(permission, userId, campaignData) {
    if (!permission || !userId) return false

    // Creator always has write access
    if (permission.createdBy === userId) {
      return true
    }

    // Campaign DM has write access to campaign resources
    if (permission.campaignId && campaignData && campaignData.dm === userId) {
      return true
    }

    // Admin users would always have access (checked elsewhere)
    // if (userIsAdmin) return true;

    // Other users don't have write access
    return false
  }

  /**
   * Get a Firebase Storage path with appropriate permissions folder
   * @param {string} resourceType - Type of resource (e.g., 'images', 'maps', 'notes')
   * @param {string} campaignId - Campaign ID
   * @param {string} permissionLevel - Permission level
   * @param {string} fileName - Name of the file
   * @returns {string} Storage path
   */
  getStoragePath(resourceType, campaignId, permissionLevel, fileName) {
    return `campaigns/${campaignId}/${resourceType}/${permissionLevel}/${fileName}`
  }
}

export default new PermissionService()
